import React, { useMemo, useState } from 'react';
import { mdiChevronDown, mdiChevronRight, mdiCodeJson } from '@mdi/js';
import Icon from '@mdi/react';
import { Variables } from '../../../..';
import * as Cd from './claim-data-detail.styles';

type TreeForceState = 'default' | 'open' | 'closed';

const CdJsonPrimitive: React.FC<{ value: unknown }> = ({ value }) => {
    if (typeof value === 'string') {
        return <Cd.CdJsonString>&quot;{value}&quot;</Cd.CdJsonString>;
    }

    if (typeof value === 'number') {
        return <Cd.CdJsonPrimitive>{value}</Cd.CdJsonPrimitive>;
    }

    if (typeof value === 'boolean') {
        return <Cd.CdJsonPrimitive>{String(value)}</Cd.CdJsonPrimitive>;
    }

    if (value === null) {
        return <Cd.CdJsonMuted>null</Cd.CdJsonMuted>;
    }

    return <Cd.CdJsonPlain>{String(value)}</Cd.CdJsonPlain>;
};

interface ICDNodeProps {
    name?: string;
    idx?: number;
    value: unknown;
    depth: number;
    last?: boolean;
    force: TreeForceState;
}

const CDNode: React.FC<ICDNodeProps> = ({ name, idx, value, depth, last, force }) => {
    const isArray = Array.isArray(value);
    const isObject = value !== null && typeof value === 'object' && !isArray;
    const isContainer = isArray || isObject;
    const initialOpen = force === 'open' ? true : force === 'closed' ? false : depth < 2;
    const [open, setOpen] = useState(initialOpen);
    const indent = 8 + depth * 18;
    const comma = last ? '' : ',';

    React.useEffect(() => {
        if (force === 'open') {
            setOpen(true);
        } else if (force === 'closed') {
            setOpen(false);
        }
    }, [force]);

    const keyPart =
        name !== undefined || idx !== undefined ? (
            <>
                {idx !== undefined ? (
                    <Cd.CdJsonPrimitive>{idx}</Cd.CdJsonPrimitive>
                ) : (
                    <Cd.CdJsonKey>&quot;{name}&quot;</Cd.CdJsonKey>
                )}
                <Cd.CdJsonColon>: </Cd.CdJsonColon>
            </>
        ) : null;

    if (!isContainer) {
        return (
            <Cd.CdTreeLeaf style={{ paddingLeft: indent }}>
                <Cd.CdTreeSpacer />
                <Cd.CdTreeCode>
                    {keyPart}
                    <CdJsonPrimitive value={value} />
                    <Cd.CdJsonColon>{comma}</Cd.CdJsonColon>
                </Cd.CdTreeCode>
            </Cd.CdTreeLeaf>
        );
    }

    const entries: Array<[string | number, unknown]> = isArray
        ? (value as unknown[]).map((entry, index) => [index, entry])
        : Object.entries(value as Record<string, unknown>);
    const openBrace = isArray ? '[' : '{';
    const closeBrace = isArray ? ']' : '}';
    const count = entries.length;

    return (
        <>
            <Cd.CdTreeLine
                $clickable
                style={{ paddingLeft: indent }}
                onClick={() => setOpen((current) => !current)}>
                <Cd.CdTreeToggle>
                    <Icon path={open ? mdiChevronDown : mdiChevronRight} size={0.75} />
                </Cd.CdTreeToggle>
                <Cd.CdTreeCode>
                    {keyPart}
                    <Cd.CdJsonColon>{openBrace}</Cd.CdJsonColon>
                    {!open && (
                        <Cd.CdJsonMuted>
                            {' '}
                            {count} {isArray ? (count === 1 ? 'item' : 'items') : count === 1 ? 'field' : 'fields'}{' '}
                            {closeBrace}
                            {comma}
                        </Cd.CdJsonMuted>
                    )}
                </Cd.CdTreeCode>
            </Cd.CdTreeLine>
            {open &&
                entries.map(([entryKey, entryValue], index) => (
                    <CDNode
                        key={String(entryKey)}
                        name={isArray ? undefined : String(entryKey)}
                        idx={isArray ? Number(entryKey) : undefined}
                        value={entryValue}
                        depth={depth + 1}
                        last={index === entries.length - 1}
                        force={force}
                    />
                ))}
            {open && (
                <Cd.CdTreeLeaf style={{ paddingLeft: indent }}>
                    <Cd.CdTreeSpacer />
                    <Cd.CdTreeCode>
                        <Cd.CdJsonColon>
                            {closeBrace}
                            {comma}
                        </Cd.CdJsonColon>
                    </Cd.CdTreeCode>
                </Cd.CdTreeLeaf>
            )}
        </>
    );
};

interface IClaimDataJsonTreeProps {
    payload: unknown;
    embedded?: boolean;
}

export const ClaimDataJsonTree: React.FunctionComponent<IClaimDataJsonTreeProps> = ({ payload, embedded }) => {
    const [force, setForce] = useState<TreeForceState>('default');
    const [version, setVersion] = useState(0);

    const applyForce = (next: TreeForceState) => {
        setForce(next);
        setVersion((current) => current + 1);
    };

    const treeKey = useMemo(() => `${version}-${force}`, [version, force]);

    return (
        <Cd.CdTreeWrap>
            <Cd.CdTreeToolbar>
                <Icon path={mdiCodeJson} size={0.62} color={Variables.ThemeLight.TextMild} />
                <Cd.CdTreeTitle>Request</Cd.CdTreeTitle>
                <Cd.CdTreeActions>
                    <Cd.CdMiniButton type="button" variant="unstyled" onClick={() => applyForce('open')}>
                        Expand all
                    </Cd.CdMiniButton>
                    <Cd.CdMiniButton type="button" variant="unstyled" onClick={() => applyForce('closed')}>
                        Collapse all
                    </Cd.CdMiniButton>
                </Cd.CdTreeActions>
            </Cd.CdTreeToolbar>
            <Cd.CdTreeScroll $embedded={embedded}>
                <Cd.CdTreeBody>
                    <CDNode key={treeKey} value={payload} depth={0} last force={force} />
                </Cd.CdTreeBody>
            </Cd.CdTreeScroll>
        </Cd.CdTreeWrap>
    );
};
